import React, { useEffect, useState } from 'react';
import {
  Flex,
  FormInput,
  Input,
  Select,
  Checkbox,
  Text,
  FormLabel,
  Box,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Heading,
  Switch,
  toast
} from '@sparrowengg/twigs-react';
import {
  InfoIcon,
  CloseIcon,
  ChevronRightIcon,
  TickCircleIcon,
  PlusIcon,
  DeleteIcon
} from '@sparrowengg/twigs-react-icons';
import { LocalDevAppDetailsProps } from '../types';
import {
  embedUrlRegex,
  youtubeRegex,
  fileUploadTypes,
  appTypeConstants
} from '../constants';
import { urlRegex } from 'commons/constants';
import { devPortalServices } from 'commons/services';
import ActionEnergy from 'commons/icons/action-energy';

const AppDetails = ({
  app,
  register,
  errors,
  watch,
  isDirty,
  setValue,
  getValues,
  setIsDisabled,
  setDeleteButtonDisabled
}: LocalDevAppDetailsProps) => {
  const icon = watch('icon');
  const ngrokUrl = watch('ngrokUrl');
  const appName = watch('appName');
  const shortDescription = watch('shortDescription');
  const appOverView = watch('appOverView');
  const appInstructions = watch('appInstructions');
  const videoUrl = watch('videoUrl');
  const supportEmail = watch('supportEmail');
  const location = watch('location');
  const events = watch('events') || [];
  const iParams = watch('iParams');
  const [productEvents, setProductEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [locations, setLocations] = useState<
    Array<{ id: number; name: string; description: string }>
  >([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [currentEvents, setCurrentEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any>({});
  const [showIParamsModal, setShowIParamsModal] = useState(false);
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const [showWhitelistedDomainsModal, setShowWhitelistedDomainsModal] =
    useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const response = await devPortalServices.getAllWebhookEvents();
        if (response?.data && Array.isArray(response.data)) {
          // Get current events value at the time of fetch
          const currentSelectedEvents = getValues('events') || [];
          const groupedEvents = response.data.map((category: any) => ({
            ...category,
            events:
              category.events?.map((event: any) => ({
                ...event,
                isChecked: currentSelectedEvents.includes(event.name)
              })) || []
          }));
          setProductEvents(groupedEvents);
          setCurrentEvents(groupedEvents);
        }
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };

    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const response = await devPortalServices.getAllLocations();
        if (response?.data && Array.isArray(response.data)) {
          setLocations(response.data);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchEvents();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update currentEvents when events change
  useEffect(() => {
    if (productEvents.length > 0) {
      const updatedEvents = productEvents.map((category: any) => ({
        ...category,
        events:
          category.events?.map((event: any) => ({
            ...event,
            isChecked: events.includes(event.name)
          })) || []
      }));
      setCurrentEvents(updatedEvents);
    }
  }, [events, productEvents]);

  // handleDisableButton is now handled in parent component (index.tsx)
  // to avoid duplicate API calls

  useEffect(() => {
    const { SIZE } = fileUploadTypes;

    if (!app) {
      if (
        SIZE in icon &&
        ngrokUrl &&
        appName &&
        shortDescription &&
        appOverView &&
        appInstructions &&
        supportEmail &&
        location
      ) {
        setIsDisabled({ publish: false, update: false });
      } else {
        setIsDisabled({ publish: true, update: false });
      }
    }
  }, [
    icon,
    ngrokUrl,
    appName,
    shortDescription,
    appOverView,
    appInstructions,
    supportEmail,
    location,
    setIsDisabled,
    app
  ]);

  // handleDisableButton is now handled in parent component (index.tsx)
  // to avoid duplicate API calls - removed this useEffect

  useEffect(() => {
    if (app && !isDirty) {
      setIsDisabled({ publish: false, update: true });
    } else {
      setIsDisabled((prev) => ({ ...prev, update: false }));
    }
  }, [isDirty, app]);

  const handleEventChange = (
    value: boolean,
    eventId: number,
    currentEventId: number,
    selectedEvent: any
  ) => {
    const updatedCurrentEvents = currentEvents.map((event: any) =>
      currentEventId === event.id
        ? {
            ...event,
            events: [
              ...event?.events?.map((subEvent: any) =>
                subEvent?.id === eventId
                  ? { ...subEvent, isChecked: value }
                  : subEvent
              )
            ]
          }
        : event
    );
    setCurrentEvents(updatedCurrentEvents);

    const currentSelectedEvents = getValues('events') || [];
    if (value) {
      if (!currentSelectedEvents.includes(selectedEvent.name)) {
        setValue('events', [...currentSelectedEvents, selectedEvent.name], {
          shouldDirty: true
        });
      }
    } else {
      setValue(
        'events',
        currentSelectedEvents.filter((e: string) => e !== selectedEvent.name),
        { shouldDirty: true }
      );
    }
  };

  const handleCloseEventsModal = () => {
    setShowEventsModal(false);
    setSelectedEvent({});
  };

  return (
    <Flex gap="$20" css={{ width: '100%' }}>
      <Flex flexDirection="column" gap="$6" css={{ flex: 1 }}>
        <FormInput
          label="App Name"
          requiredIndicator
          {...register('appName', {
            required: 'App Name is required',
            validate: {
              matchPattern: (name: string) => {
                return (
                  /^[A-Za-z0-9\s]+$/.test(name) || 'Please enter a valid name'
                );
              }
            }
          })}
          size="lg"
          maxLength={50}
          value={watch('appName')}
          showCount
          error={errors?.appName?.message}
          placeholder="Ex: Survey Sparrow"
        />
        <FormInput
          label="Short Description"
          requiredIndicator
          {...register('shortDescription', {
            required: 'Short Description is required'
          })}
          size="lg"
          maxLength={100}
          showCount
          value={watch('shortDescription')}
          error={errors?.shortDescription?.message}
        />
        <FormInput
          label="App Overview"
          requiredIndicator
          {...register('appOverView', {
            required: 'App Overview is required'
          })}
          as="textarea"
          size="lg"
          maxLength={1000}
          showCount
          value={watch('appOverView')}
          css={{ resize: 'none', height: '$24' }}
          error={errors?.appOverView?.message}
        />
        <FormInput
          label="App Instructions"
          requiredIndicator
          {...register('appInstructions', {
            required: 'App Instructions is required'
          })}
          as="textarea"
          size="lg"
          maxLength={2000}
          value={watch('appInstructions')}
          showCount
          css={{ resize: 'none', height: '$24' }}
          error={errors.appInstructions?.message}
        />
        <FormInput
          label="Video URL"
          {...register('videoUrl', {
            validate: (value) => {
              // Only validate if a value is provided
              if (value && value.trim()) {
                if (!(embedUrlRegex.test(value) || youtubeRegex.test(value))) {
                  return 'Please enter a valid YouTube URL/embed URL';
                }
              }
              return true;
            }
          })}
          size="lg"
          maxLength={200}
          value={watch('videoUrl')}
          error={errors.videoUrl?.message}
          placeholder="Ex: https://www.youtube.com/watch?v=eXdQW-P8zHs (Optional)"
        />
        <FormInput
          label="Support URL"
          {...register('supportUrl', {
            required: false,
            pattern: {
              value: urlRegex,
              message: 'Please enter a valid URL'
            }
          })}
          size="lg"
          maxLength={200}
          error={errors?.supportUrl?.message}
          placeholder="Ex: https://support.surveysparrow.com"
        />
        <FormInput
          label="Website Link"
          {...register('websiteLink', {
            pattern: {
              value: urlRegex,
              message: 'Please enter a valid URL'
            }
          })}
          size="lg"
          maxLength={200}
          error={errors?.websiteLink?.message}
          placeholder="http://app.samplesparrow.com"
        />
      </Flex>
      <Flex flexDirection="column" gap="$6" css={{ flex: 1 }}>
        <FormInput
          label="Enter your ngrok URL"
          requiredIndicator
          {...register('ngrokUrl', {
            required: 'Ngrok URL is required',
            pattern: {
              value: urlRegex,
              message: 'Please enter a valid URL'
            }
          })}
          size="lg"
          maxLength={200}
          value={watch('ngrokUrl')}
          error={errors?.ngrokUrl?.message}
          placeholder="Ex: https://abc123.ngrok.io"
        />
        <FormInput
          label="Support Email"
          requiredIndicator
          {...register('supportEmail', {
            required: 'Support Email is required',
            validate: {
              matchPattern: (email: string) =>
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
                  email
                ) || 'Email address must be a valid address'
            }
          })}
          size="lg"
          maxLength={200}
          error={errors.supportEmail?.message}
          placeholder="Ex: support@surveysparrow.com"
        />
        <Box css={{ marginTop: '$4' }}>
          <Flex alignItems="center" gap="$2" css={{ marginBottom: '$2' }}>
            <FormLabel requiredIndicator>Location</FormLabel>
            {location &&
              locations.find((loc) => loc.name === location)?.description && (
                <Tooltip
                  content={
                    locations.find((loc) => loc.name === location)
                      ?.description || ''
                  }
                  size="sm"
                  side="right"
                >
                  <Box
                    css={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '$neutral500',
                      '&:hover': {
                        color: '$neutral700'
                      }
                    }}
                  >
                    <InfoIcon size={16} />
                  </Box>
                </Tooltip>
              )}
          </Flex>
          <Select
            size="lg"
            placeholder={
              loadingLocations ? 'Loading locations...' : 'Select Location'
            }
            options={locations.map((loc) => ({
              value: loc.name,
              label: loc.name
            }))}
            value={
              locations.find((loc) => loc.name === location)
                ? {
                    value: location,
                    label: location
                  }
                : null
            }
            onChange={(selected: any) => {
              setValue('location', selected?.value || '', {
                shouldDirty: true,
                shouldValidate: true
              });
            }}
            isSearchable={false}
            isDisabled={loadingLocations}
            css={{
              '& .twigs-select__menu-list': {
                maxHeight: '300px !important',
                overflowY: 'auto !important'
              },
              '& .twigs-select__menu': {
                zIndex: 9999
              }
            }}
          />
          {errors?.location && (
            <Text size="sm" css={{ color: '$error500', marginTop: '$2' }}>
              {errors.location.message}
            </Text>
          )}
        </Box>
        <Box css={{ marginTop: '$4' }}>
          <FormLabel>Product Events</FormLabel>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowEventsModal(true)}
            css={{
              width: '100%',
              justifyContent: 'space-between',
              marginTop: '$4'
            }}
            rightIcon={
              events.length > 0 ? (
                <Flex
                  alignItems="center"
                  gap="$2"
                  css={{
                    background: '$primary100',
                    padding: '3px $2',
                    borderRadius: '$pill',
                    height: '$5'
                  }}
                >
                  <TickCircleIcon size={14} color="#3B8088" />
                  <Text size="sm" css={{ color: '$primary700' }}>
                    {events.length}
                  </Text>
                </Flex>
              ) : (
                <ChevronRightIcon size={20} />
              )
            }
          >
            {events.length > 0
              ? `${events.length} event${events.length > 1 ? 's' : ''} selected`
              : 'Select Events'}
          </Button>
          {errors?.events && (
            <Text size="sm" css={{ color: '$error500', marginTop: '$2' }}>
              {errors.events.message}
            </Text>
          )}
        </Box>
        <Box css={{ marginTop: '$4' }}>
          <FormLabel>IParams</FormLabel>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowIParamsModal(true)}
            css={{
              width: '100%',
              justifyContent: 'space-between',
              marginTop: '$4'
            }}
            rightIcon={<ChevronRightIcon size={20} />}
          >
            {(() => {
              const iParams = watch('iParams');
              const paramsObj =
                typeof iParams === 'string'
                  ? (() => {
                      try {
                        return JSON.parse(iParams);
                      } catch {
                        return {};
                      }
                    })()
                  : iParams || {};
              const count = Object.keys(paramsObj).length;
              return count > 0
                ? `${count} param${count > 1 ? 's' : ''} configured`
                : 'Configure IParams';
            })()}
          </Button>
          {errors?.iParams && (
            <Text size="sm" css={{ color: '$error500', marginTop: '$2' }}>
              {errors.iParams.message}
            </Text>
          )}
        </Box>
        <Box css={{ marginTop: '$4' }}>
          <FormLabel>OAuth</FormLabel>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowOAuthModal(true)}
            css={{
              width: '100%',
              justifyContent: 'space-between',
              marginTop: '$4'
            }}
            rightIcon={<ChevronRightIcon size={20} />}
          >
            {watch('oauth') &&
            typeof watch('oauth') === 'object' &&
            Object.keys(watch('oauth') || {}).length > 0
              ? 'OAuth Configured'
              : 'Configure OAuth'}
          </Button>
          {errors?.oauth && (
            <Text size="sm" css={{ color: '$error500', marginTop: '$2' }}>
              {errors.oauth.message}
            </Text>
          )}
        </Box>
        <Box css={{ marginTop: '$4' }}>
          <FormLabel>Whitelisted Domains</FormLabel>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowWhitelistedDomainsModal(true)}
            css={{
              width: '100%',
              justifyContent: 'space-between',
              marginTop: '$4'
            }}
            rightIcon={<ChevronRightIcon size={20} />}
          >
            {(() => {
              const domains = watch('whitelistedDomains');
              const domainsArray = Array.isArray(domains) ? domains : [];
              return domainsArray.length > 0
                ? `${domainsArray.length} domain${
                    domainsArray.length > 1 ? 's' : ''
                  } configured`
                : 'Configure Whitelisted Domains';
            })()}
          </Button>
          {errors?.whitelistedDomains && (
            <Text size="sm" css={{ color: '$error500', marginTop: '$2' }}>
              {errors.whitelistedDomains.message}
            </Text>
          )}
        </Box>
      </Flex>
      {showEventsModal && (
        <EventsModal
          currentEvents={currentEvents}
          loadingEvents={loadingEvents}
          selectedEvent={selectedEvent}
          onSelectEvent={setSelectedEvent}
          onEventChange={handleEventChange}
          onClose={handleCloseEventsModal}
        />
      )}
      {showIParamsModal && (
        <IParamsModal
          iParams={(() => {
            const iParams = watch('iParams');
            if (typeof iParams === 'string') {
              try {
                return JSON.parse(iParams);
              } catch {
                return {};
              }
            }
            return iParams || {};
          })()}
          onSave={(params) => {
            setValue('iParams', params, { shouldDirty: true });
            setShowIParamsModal(false);
          }}
          onClose={() => setShowIParamsModal(false)}
        />
      )}
      {showOAuthModal && (
        <OAuthModal
          oauth={(() => {
            const oauth = watch('oauth');
            if (typeof oauth === 'string') {
              try {
                return JSON.parse(oauth);
              } catch {
                return {};
              }
            }
            return oauth || {};
          })()}
          onSave={(oauth) => {
            setValue('oauth', oauth, { shouldDirty: true });
            setShowOAuthModal(false);
          }}
          onClose={() => setShowOAuthModal(false)}
        />
      )}
      {showWhitelistedDomainsModal && (
        <WhitelistedDomainsModal
          domains={(() => {
            const domains = watch('whitelistedDomains');
            return Array.isArray(domains) ? domains : [];
          })()}
          onSave={(domains) => {
            setValue('whitelistedDomains', domains, { shouldDirty: true });
            setShowWhitelistedDomainsModal(false);
          }}
          onClose={() => setShowWhitelistedDomainsModal(false)}
        />
      )}
    </Flex>
  );
};

const EventsModal = ({
  currentEvents,
  loadingEvents,
  selectedEvent,
  onSelectEvent,
  onEventChange,
  onClose
}: {
  currentEvents: any[];
  loadingEvents: boolean;
  selectedEvent: any;
  onSelectEvent: (event: any) => void;
  onEventChange: (
    value: boolean,
    eventId: number,
    currentEventId: number,
    selectedEvent: any
  ) => void;
  onClose: () => void;
}) => {
  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 900,
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Fixed Header */}
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200',
            flexShrink: 0,
            background: '$white900',
            zIndex: 1
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Select Product Events</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        {/* Scrollable Content */}
        <Flex
          css={{
            padding: '$12',
            gap: '$12',
            flex: 1,
            overflow: 'hidden',
            minHeight: 0
          }}
        >
          <Box
            css={{
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: '$neutral100',
                borderRadius: '$md'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '$neutral300',
                borderRadius: '$md',
                '&:hover': {
                  background: '$neutral400'
                }
              }
            }}
          >
            {loadingEvents ? (
              <Flex
                alignItems="center"
                justifyContent="center"
                css={{ padding: '$20' }}
              >
                <Text size="sm" css={{ color: '$neutral600' }}>
                  Loading events...
                </Text>
              </Flex>
            ) : currentEvents.length > 0 ? (
              <Flex flexDirection="column" gap="$4">
                {currentEvents.map((category: any) => {
                  const count =
                    category.events?.filter((event: any) => event?.isChecked)
                      .length || 0;
                  return (
                    <EventCategoryItem
                      key={category.id}
                      category={category}
                      count={count}
                      onEventChange={onEventChange}
                      onSelectEvent={onSelectEvent}
                    />
                  );
                })}
              </Flex>
            ) : (
              <Flex
                alignItems="center"
                justifyContent="center"
                css={{ padding: '$20' }}
              >
                <Text size="sm" css={{ color: '$neutral600' }}>
                  No events available
                </Text>
              </Flex>
            )}
          </Box>
          <Box
            css={{
              flex: 1,
              borderRadius: '$lg',
              background: '$secondary700',
              color: '$white900',
              padding: '$12',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              css={{
                paddingBottom: '$8',
                borderBottom: '$borderWidths$xs solid rgba(255, 255, 255, 0.1)',
                marginBottom: '$8',
                flexShrink: 0
              }}
            >
              <Text size="md" weight="bold">
                Sample Event Payload (JSON)
              </Text>
              {selectedEvent?.name && (
                <Flex alignItems="center" gap="$4">
                  <Box
                    css={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '$pill',
                      background: '$primary400'
                    }}
                  />
                  <Text size="sm" weight="medium">
                    {selectedEvent.name}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Box
              css={{
                flex: 1,
                overflow: 'auto',
                background: '#444B5A',
                borderRadius: '$md',
                padding: '$8',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  background: '#3a3f4a',
                  borderRadius: '$md'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#5a5f6a',
                  borderRadius: '$md',
                  '&:hover': {
                    background: '#6a6f7a'
                  }
                }
              }}
            >
              {selectedEvent?.name ? (
                <Text
                  as="pre"
                  size="sm"
                  css={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: '$white900',
                    fontFamily: 'monospace',
                    fontSize: '$sm',
                    lineHeight: '$md'
                  }}
                >
                  {JSON.stringify(selectedEvent?.payload || {}, null, 2)}
                </Text>
              ) : (
                <Text size="sm" css={{ color: '$neutral400' }}>
                  Please click on an event to view the sample event payload
                </Text>
              )}
            </Box>
          </Box>
        </Flex>
        {/* Fixed Footer */}
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          css={{
            padding: '$12',
            paddingTop: '$8',
            gap: '$4',
            borderTop: '$borderWidths$xs solid $neutral200',
            flexShrink: 0,
            background: '$white900',
            zIndex: 1
          }}
        >
          <Button size="lg" variant="outline" onClick={onClose}>
            Save
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

const EventCategoryItem = ({
  category,
  count,
  onEventChange,
  onSelectEvent
}: {
  category: any;
  count: number;
  onEventChange: (
    value: boolean,
    eventId: number,
    currentEventId: number,
    selectedEvent: any
  ) => void;
  onSelectEvent: (event: any) => void;
}) => {
  const [isActive, setIsActive] = useState(false);

  return (
    <Flex flexDirection="column">
      <Flex
        justifyContent="space-between"
        css={{
          padding: '$4',
          cursor: 'pointer',
          borderRadius: '$lg',
          '&:hover': {
            background: '$neutral50'
          }
        }}
        onClick={() => setIsActive(!isActive)}
      >
        <Flex alignItems="center" gap="$2">
          <ActionEnergy color="#64748B" size={16} />
          <Text size="md">{category?.name}</Text>
        </Flex>
        <Flex alignItems="center" gap="$4">
          {count > 0 && (
            <Flex
              alignItems="center"
              gap="$2"
              css={{
                background: '$primary100',
                padding: '3px $2',
                borderRadius: '$pill',
                height: '$5'
              }}
            >
              <TickCircleIcon size={14} color="#3B8088" />
              <Text size="sm" css={{ color: '$primary700' }}>
                {count}
              </Text>
            </Flex>
          )}
          <Box
            css={{
              transform: `rotate(${isActive ? '90deg' : '0deg'})`,
              transition: 'all .3s ease'
            }}
          >
            <ChevronRightIcon
              size={24}
              color={isActive ? '#56B0BB' : '#6A6A6A'}
            />
          </Box>
        </Flex>
      </Flex>
      {isActive && (
        <Flex flexDirection="column" css={{ paddingLeft: '$12' }} gap="$2">
          {category?.events?.map((event: any) => {
            return (
              <Flex
                key={event?.id}
                alignItems="center"
                onClick={() => onSelectEvent(event)}
                css={{
                  padding: '$4',
                  cursor: 'pointer',
                  borderRadius: '$lg',
                  background: event?.isChecked ? '$primary50' : 'transparent',
                  '&:hover': {
                    background: '$primary50'
                  }
                }}
              >
                <Flex
                  alignItems="center"
                  gap="$4"
                  css={{
                    button: {
                      border: '$borderWidths$xs solid $primary400 !important',
                      '&[data-state="checked"]': {
                        background: '$primary500 !important'
                      }
                    }
                  }}
                >
                  <Checkbox
                    size="sm"
                    checked={event?.isChecked}
                    css={{ cursor: 'pointer' }}
                    onChange={(value) =>
                      onEventChange(value as boolean, event?.id, category?.id, {
                        name: event.name,
                        payload: event.payload
                      })
                    }
                  />
                  <Text size="sm">{event.name}</Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

const IParamsModal = ({
  iParams,
  onSave,
  onClose
}: {
  iParams: Record<string, any>;
  onSave: (params: Record<string, any>) => void;
  onClose: () => void;
}) => {
  // Use stable IDs for React keys, separate from the actual param keys
  const [paramIds, setParamIds] = useState<string[]>(() => {
    const baseTime = Date.now();
    return Object.keys(iParams || {}).map(
      (key, index) => `param_${baseTime}_${index}_${key}`
    );
  });
  const [params, setParams] = useState<Record<string, any>>(() => {
    // Initialize params with stable IDs as keys
    const initialParams: Record<string, any> = {};
    const baseTime = Date.now();
    Object.keys(iParams || {}).forEach((key, index) => {
      const id = `param_${baseTime}_${index}_${key}`;
      initialParams[id] = iParams[key];
    });
    return initialParams;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paramKeys, setParamKeys] = useState<Record<string, string>>(() => {
    // Initialize param keys mapping - map stable IDs to actual keys
    const keys: Record<string, string> = {};
    const baseTime = Date.now();
    Object.keys(iParams || {}).forEach((key, index) => {
      const id = `param_${baseTime}_${index}_${key}`;
      keys[id] = key;
    });
    return keys;
  });

  const handleAddParam = () => {
    // Check if the last param is complete before adding a new one
    if (paramIds.length > 0) {
      const lastId = paramIds[paramIds.length - 1];
      const lastKey = paramKeys[lastId]?.trim() || '';
      const lastParam = params[lastId];

      if (
        !lastKey ||
        !lastParam?.display_name?.trim() ||
        !lastParam?.type?.trim()
      ) {
        toast({
          variant: 'error',
          description:
            'Please complete the current param before adding a new one'
        });
        return;
      }
    }

    const newId = `param_${Date.now()}`;
    const newKey = '';
    setParamIds([...paramIds, newId]);
    setParams({
      ...params,
      [newId]: {
        display_name: '',
        required: false,
        type: 'text'
      }
    });
    setParamKeys({
      ...paramKeys,
      [newId]: newKey
    });
  };

  const handleRemoveParam = (id: string) => {
    const key = paramKeys[id] || id;
    const newParams = { ...params };
    const newKeys = { ...paramKeys };
    delete newParams[id];
    delete newKeys[id];
    setParamIds(paramIds.filter((pid) => pid !== id));
    setParams(newParams);
    setParamKeys(newKeys);
  };

  const handleUpdateParamKey = (id: string, newKey: string) => {
    // Always update paramKeys immediately for smooth typing
    setParamKeys({
      ...paramKeys,
      [id]: newKey
    });
  };

  const handleParamKeyBlur = (id: string) => {
    // No need to restructure - we keep using stable IDs
    // The paramKeys mapping is already updated in handleUpdateParamKey
    // Just validate that the key is not empty and not duplicate
    const newKey = paramKeys[id]?.trim() || '';
    if (newKey) {
      // Check for duplicate keys
      const duplicateId = Object.keys(paramKeys).find(
        (pid) => pid !== id && paramKeys[pid]?.trim() === newKey
      );
      if (duplicateId) {
        setErrors({
          ...errors,
          [`${id}_key`]: 'Param key must be unique'
        });
      } else if (errors[`${id}_key`] === 'Param key must be unique') {
        // Clear duplicate error if fixed
        const newErrors = { ...errors };
        delete newErrors[`${id}_key`];
        setErrors(newErrors);
      }
    }
  };

  const handleUpdateParam = (id: string, field: string, value: any) => {
    setParams({
      ...params,
      [id]: {
        ...params[id],
        [field]: value
      }
    });
    // Clear error for this field
    if (errors[`${id}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${id}_${field}`];
      setErrors(newErrors);
    }
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};
    const finalParams: Record<string, any> = {};

    // Validate and build final params using stable IDs
    paramIds.forEach((id) => {
      const param = params[id];
      const finalKey = paramKeys[id]?.trim() || '';

      if (!finalKey) {
        newErrors[`${id}_key`] = 'Param key is required';
      }
      if (!param?.display_name?.trim()) {
        newErrors[`${id}_display_name`] = 'Display name is required';
      }
      if (!param?.type?.trim()) {
        newErrors[`${id}_type`] = 'Type is required';
      }

      // Check for duplicate keys
      if (finalKey && finalParams[finalKey]) {
        newErrors[`${id}_key`] = 'Param key must be unique';
      }

      if (
        !newErrors[`${id}_key`] &&
        !newErrors[`${id}_display_name`] &&
        !newErrors[`${id}_type`] &&
        finalKey
      ) {
        finalParams[finalKey] = {
          display_name: param.display_name.trim(),
          required: param.required || false,
          type: param.type.trim()
        };
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(finalParams);
  };

  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 700,
          maxWidth: '90vw',
          maxHeight: '90vh'
        }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Configure IParams</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        <Box
          css={{
            padding: '$12',
            maxHeight: 'calc(90vh - 180px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '$neutral100',
              borderRadius: '$md'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '$neutral300',
              borderRadius: '$md',
              '&:hover': {
                background: '$neutral400'
              }
            }
          }}
        >
          <Flex flexDirection="column" gap="$6">
            {paramIds.map((id) => (
              <Box
                key={id}
                css={{
                  padding: '$8',
                  border: '$borderWidths$xs solid $neutral200',
                  borderRadius: '$lg'
                }}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  css={{ marginBottom: '$6' }}
                >
                  <Text size="md" weight="bold">
                    Param Configuration
                  </Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    color="error"
                    icon={<DeleteIcon />}
                    onClick={() => handleRemoveParam(id)}
                  />
                </Flex>
                <Flex flexDirection="column" gap="$4">
                  <FormInput
                    label="Param Key"
                    requiredIndicator
                    size="lg"
                    value={paramKeys[id] || ''}
                    onChange={(e: any) =>
                      handleUpdateParamKey(id, e.target.value)
                    }
                    onBlur={() => handleParamKeyBlur(id)}
                    error={errors[`${id}_key`]}
                    placeholder="e.g., zendesk_sub_domain"
                  />
                  <FormInput
                    label="Display Name"
                    requiredIndicator
                    size="lg"
                    value={params[id]?.display_name || ''}
                    onChange={(e: any) =>
                      handleUpdateParam(id, 'display_name', e.target.value)
                    }
                    error={errors[`${id}_display_name`]}
                  />
                  <Box>
                    <FormLabel>Type</FormLabel>
                    <Select
                      size="lg"
                      options={[
                        { value: 'text', label: 'Text' },
                        { value: 'api_key', label: 'API Key' }
                      ]}
                      value={
                        params[id]?.type
                          ? {
                              value: params[id].type,
                              label:
                                params[id].type === 'api_key'
                                  ? 'API Key'
                                  : 'Text'
                            }
                          : null
                      }
                      onChange={(selected: any) =>
                        handleUpdateParam(id, 'type', selected?.value || 'text')
                      }
                    />
                    {errors[`${id}_type`] && (
                      <Text
                        size="sm"
                        css={{ color: '$error500', marginTop: '$2' }}
                      >
                        {errors[`${id}_type`]}
                      </Text>
                    )}
                  </Box>
                  <Checkbox
                    checked={params[id]?.required || false}
                    onChange={(value) =>
                      handleUpdateParam(id, 'required', value)
                    }
                  >
                    <Text size="md">Required</Text>
                  </Checkbox>
                </Flex>
              </Box>
            ))}
            {paramIds.length === 0 && (
              <Text
                size="sm"
                css={{
                  color: '$neutral600',
                  textAlign: 'center',
                  padding: '$8'
                }}
              >
                No params configured. Click "Add Param" to add one.
              </Text>
            )}
            <Button
              variant="outline"
              size="md"
              leftIcon={<PlusIcon />}
              onClick={handleAddParam}
              css={{ width: 'max-content', alignSelf: 'flex-start' }}
            >
              Add Param
            </Button>
          </Flex>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          css={{
            padding: '$12',
            paddingTop: '$8',
            gap: '$4',
            borderTop: '$borderWidths$xs solid $neutral200'
          }}
        >
          <Button size="lg" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button size="lg" color="secondary" onClick={handleSave}>
            Save
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

const OAuthModal = ({
  oauth,
  onSave,
  onClose
}: {
  oauth: any;
  onSave: (oauth: any) => void;
  onClose: () => void;
}) => {
  // Extract existing options, preserving all fields including additional ones
  const existingOptions = oauth?.options || {};
  const [formData, setFormData] = useState<any>({
    client_id: oauth?.client_id || '',
    client_secret: oauth?.client_secret || '',
    authorize_url: oauth?.authorize_url || '',
    token_url: oauth?.token_url || '',
    options: {
      response_type: existingOptions.response_type || 'code',
      scope: existingOptions.scope || [],
      // Preserve any additional options
      ...Object.keys(existingOptions).reduce((acc: any, key) => {
        if (!['response_type', 'scope'].includes(key)) {
          acc[key] = existingOptions[key];
        }
        return acc;
      }, {})
    }
  });
  const [scopeInput, setScopeInput] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Additional options state (key-value pairs)
  const [additionalOptionIds, setAdditionalOptionIds] = useState<string[]>(
    () => {
      const baseTime = Date.now();
      const additionalKeys = Object.keys(existingOptions).filter(
        (key) => !['response_type', 'scope'].includes(key)
      );
      return additionalKeys.map(
        (key, index) => `option_${baseTime}_${index}_${key}`
      );
    }
  );
  const [additionalOptions, setAdditionalOptions] = useState<
    Record<string, { key: string; value: any }>
  >(() => {
    const baseTime = Date.now();
    const options: Record<string, { key: string; value: any }> = {};
    Object.keys(existingOptions).forEach((key, index) => {
      if (
        ![
          'response_type',
          'scope',
          'code_verifier',
          'code_challenge_method'
        ].includes(key)
      ) {
        const id = `option_${baseTime}_${index}_${key}`;
        options[id] = {
          key,
          value:
            typeof existingOptions[key] === 'object'
              ? JSON.stringify(existingOptions[key], null, 2)
              : String(existingOptions[key])
        };
      }
    });
    return options;
  });

  const handleChange = (field: string, value: any) => {
    if (field.startsWith('options.')) {
      const optionField = field.replace('options.', '');
      setFormData({
        ...formData,
        options: {
          ...formData.options,
          [optionField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
    // Clear error
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleAddScope = () => {
    if (scopeInput.trim()) {
      handleChange('options.scope', [
        ...(formData.options.scope || []),
        scopeInput.trim()
      ]);
      setScopeInput('');
    }
  };

  const handleRemoveScope = (index: number) => {
    const newScopes = [...(formData.options.scope || [])];
    newScopes.splice(index, 1);
    handleChange('options.scope', newScopes);
  };

  const handleAddAdditionalOption = () => {
    // Check if the last option is complete before adding a new one
    if (additionalOptionIds.length > 0) {
      const lastId = additionalOptionIds[additionalOptionIds.length - 1];
      const lastOption = additionalOptions[lastId];

      if (!lastOption?.key?.trim() || !lastOption?.value?.trim()) {
        toast({
          variant: 'error',
          description:
            'Please complete the current option (key and value) before adding a new one'
        });
        return;
      }
    }

    const newId = `option_${Date.now()}`;
    setAdditionalOptionIds([...additionalOptionIds, newId]);
    setAdditionalOptions({
      ...additionalOptions,
      [newId]: { key: '', value: '' }
    });
  };

  const handleRemoveAdditionalOption = (id: string) => {
    const newOptions = { ...additionalOptions };
    delete newOptions[id];
    setAdditionalOptions(newOptions);
    setAdditionalOptionIds(additionalOptionIds.filter((optId) => optId !== id));
  };

  const handleUpdateAdditionalOption = (
    id: string,
    field: 'key' | 'value',
    value: any
  ) => {
    setAdditionalOptions({
      ...additionalOptions,
      [id]: {
        ...additionalOptions[id],
        [field]: value
      }
    });
  };

  // Prevent adding reserved keys as additional options
  const isReservedKey = (key: string): boolean => {
    const reservedKeys = [
      'response_type',
      'scope',
      'code_verifier',
      'code_challenge_method'
    ];
    return reservedKeys.includes(key.trim().toLowerCase());
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.client_id?.trim()) {
      newErrors.client_id = 'Client ID is required';
    }
    if (!formData.client_secret?.trim()) {
      newErrors.client_secret = 'Client Secret is required';
    }
    if (!formData.authorize_url?.trim()) {
      newErrors.authorize_url = 'Authorize URL is required';
    }
    if (!formData.token_url?.trim()) {
      newErrors.token_url = 'Token URL is required';
    }

    // Validate that no reserved keys are used in additional options
    additionalOptionIds.forEach((id) => {
      const option = additionalOptions[id];
      if (option?.key?.trim() && isReservedKey(option.key)) {
        newErrors[`additional_option_${id}`] =
          'This key is reserved and cannot be used';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build options object - always include response_type as "code" and scope
    const options: any = {
      response_type: 'code',
      scope: formData.options.scope || []
    };

    // Add additional options (but prevent overriding reserved keys)
    const reservedKeys = ['response_type', 'scope'];
    additionalOptionIds.forEach((id) => {
      const option = additionalOptions[id];
      if (option?.key?.trim()) {
        const key = option.key.trim();
        // Skip reserved keys to prevent overriding defaults (case-insensitive check)
        if (reservedKeys.includes(key.toLowerCase())) {
          return;
        }
        let value = option.value;
        // Try to parse as JSON if it looks like JSON, otherwise use as string
        if (typeof value === 'string' && value.trim()) {
          try {
            value = JSON.parse(value);
          } catch {
            // If parsing fails, use as string
            value = value.trim();
          }
        }
        options[key] = value;
      }
    });

    const finalOAuth: any = {
      client_id: formData.client_id.trim(),
      client_secret: formData.client_secret.trim(),
      authorize_url: formData.authorize_url.trim(),
      token_url: formData.token_url.trim(),
      options
    };

    onSave(finalOAuth);
    toast({
      variant: 'success',
      description: 'OAuth configuration saved successfully'
    });
  };

  const handleClear = () => {
    // Check if there's any data to clear
    const hasData =
      formData.client_id?.trim() ||
      formData.client_secret?.trim() ||
      formData.authorize_url?.trim() ||
      formData.token_url?.trim() ||
      (formData.options?.scope && formData.options.scope.length > 0) ||
      additionalOptionIds.length > 0;

    setFormData({
      client_id: '',
      client_secret: '',
      authorize_url: '',
      token_url: '',
      options: {
        response_type: 'code',
        scope: []
      }
    });
    setScopeInput('');
    setAdditionalOptionIds([]);
    setAdditionalOptions({});
    setErrors({});

    // Only show toast if there was data to clear
    if (hasData) {
      toast({
        variant: 'default',
        description: 'OAuth configuration cleared'
      });
    }
  };

  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 700,
          maxWidth: '90vw',
          maxHeight: '90vh'
        }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Configure OAuth</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        <Box
          css={{
            padding: '$12',
            maxHeight: 'calc(90vh - 180px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '$neutral100',
              borderRadius: '$md'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '$neutral300',
              borderRadius: '$md',
              '&:hover': {
                background: '$neutral400'
              }
            }
          }}
        >
          <Flex flexDirection="column" gap="$6">
            <FormInput
              label="Client ID"
              requiredIndicator
              size="lg"
              value={formData.client_id}
              onChange={(e: any) => handleChange('client_id', e.target.value)}
              error={errors.client_id}
            />
            <FormInput
              label="Client Secret"
              requiredIndicator
              size="lg"
              type="password"
              value={formData.client_secret}
              onChange={(e: any) =>
                handleChange('client_secret', e.target.value)
              }
              error={errors.client_secret}
            />
            <FormInput
              label="Authorize URL"
              requiredIndicator
              size="lg"
              value={formData.authorize_url}
              onChange={(e: any) =>
                handleChange('authorize_url', e.target.value)
              }
              error={errors.authorize_url}
            />
            <FormInput
              label="Token URL"
              requiredIndicator
              size="lg"
              value={formData.token_url}
              onChange={(e: any) => handleChange('token_url', e.target.value)}
              error={errors.token_url}
            />
            <Box>
              <FormLabel>Scopes</FormLabel>
              <Flex gap="$2" css={{ marginTop: '$4' }} alignItems="center">
                <FormInput
                  size="lg"
                  value={scopeInput}
                  onChange={(e: any) => setScopeInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddScope();
                    }
                  }}
                  placeholder="Enter scope and press Enter"
                  css={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    '& input': {
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      '&::placeholder': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }
                    }
                  }}
                />
                <Button
                  onClick={handleAddScope}
                  leftIcon={<PlusIcon />}
                  size="md"
                >
                  Add
                </Button>
              </Flex>
              {formData.options?.scope?.length > 0 && (
                <Flex gap="$2" css={{ marginTop: '$4', flexWrap: 'wrap' }}>
                  {formData.options.scope.map(
                    (scope: string, index: number) => (
                      <Flex
                        key={index}
                        alignItems="center"
                        gap="$2"
                        css={{
                          padding: '$2 $4',
                          background: '$primary50',
                          borderRadius: '$md',
                          border: '$borderWidths$xs solid $primary200'
                        }}
                      >
                        <Text size="sm">{scope}</Text>
                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={<CloseIcon size={12} />}
                          onClick={() => handleRemoveScope(index)}
                        />
                      </Flex>
                    )
                  )}
                </Flex>
              )}
            </Box>
            <Box>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                css={{ marginBottom: '$4' }}
              >
                <FormLabel>Additional Options</FormLabel>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<PlusIcon />}
                  onClick={handleAddAdditionalOption}
                >
                  Add Option
                </Button>
              </Flex>
              <Flex flexDirection="column" gap="$3">
                {/* Default response_type option */}
                <Flex alignItems="center" gap="$2">
                  <Text
                    size="sm"
                    css={{
                      color: '$neutral700',
                      fontFamily: 'monospace',
                      minWidth: '120px'
                    }}
                  >
                    response_type:
                  </Text>
                  <Text
                    size="sm"
                    css={{
                      color: '$neutral900',
                      fontFamily: 'monospace'
                    }}
                  >
                    code
                  </Text>
                </Flex>
                {/* Additional user-defined options */}
                {additionalOptionIds.length > 0 && (
                  <>
                    {additionalOptionIds.map((id) => {
                      const hasKeyError =
                        additionalOptions[id]?.key &&
                        isReservedKey(additionalOptions[id].key);
                      return (
                        <Flex
                          key={id}
                          alignItems="flex-start"
                          gap="$2"
                          css={{
                            padding: '$2 0',
                            position: 'relative'
                          }}
                        >
                          <FormInput
                            placeholder="Key"
                            size="md"
                            value={additionalOptions[id]?.key || ''}
                            onChange={(e: any) =>
                              handleUpdateAdditionalOption(
                                id,
                                'key',
                                e.target.value
                              )
                            }
                            css={{
                              width: '180px',
                              fontFamily: 'monospace',
                              fontSize: '$sm',
                              '& input': {
                                padding: '$3 $4',
                                borderRadius: '$md',
                                border: '$borderWidths$xs solid $neutral300',
                                transition: 'all 0.2s ease',
                                '&:focus': {
                                  borderColor: '$primary500',
                                  boxShadow: '0 0 0 3px $primary100'
                                },
                                '&:hover:not(:focus)': {
                                  borderColor: '$neutral400'
                                }
                              }
                            }}
                            error={
                              hasKeyError
                                ? 'This key is reserved and cannot be used'
                                : undefined
                            }
                          />
                          <Text
                            size="md"
                            css={{
                              color: '$neutral600',
                              fontFamily: 'monospace',
                              marginTop: '$4',
                              fontWeight: '500'
                            }}
                          >
                            :
                          </Text>
                          <FormInput
                            placeholder="Value"
                            size="md"
                            value={additionalOptions[id]?.value || ''}
                            onChange={(e: any) =>
                              handleUpdateAdditionalOption(
                                id,
                                'value',
                                e.target.value
                              )
                            }
                            css={{
                              flex: 1,
                              fontFamily: 'monospace',
                              fontSize: '$sm',
                              '& input': {
                                padding: '$3 $4',
                                borderRadius: '$md',
                                border: '$borderWidths$xs solid $neutral300',
                                transition: 'all 0.2s ease',
                                '&:focus': {
                                  borderColor: '$primary500',
                                  boxShadow: '0 0 0 3px $primary100'
                                },
                                '&:hover:not(:focus)': {
                                  borderColor: '$neutral400'
                                }
                              }
                            }}
                          />
                          <IconButton
                            size="sm"
                            variant="ghost"
                            color="error"
                            icon={<DeleteIcon size={16} />}
                            onClick={() => handleRemoveAdditionalOption(id)}
                            css={{ marginTop: '$1' }}
                          />
                        </Flex>
                      );
                    })}
                  </>
                )}
              </Flex>
            </Box>
          </Flex>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          css={{
            padding: '$12',
            paddingTop: '$8',
            gap: '$4',
            borderTop: '$borderWidths$xs solid $neutral200'
          }}
        >
          <Button
            size="lg"
            variant="outline"
            color="error"
            onClick={handleClear}
          >
            Clear
          </Button>
          <Flex gap="$4">
            <Button size="lg" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button size="lg" color="secondary" onClick={handleSave}>
              Save
            </Button>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

const WhitelistedDomainsModal = ({
  domains,
  onSave,
  onClose
}: {
  domains: string[];
  onSave: (domains: string[]) => void;
  onClose: () => void;
}) => {
  // Use stable IDs for React keys
  const [domainIds, setDomainIds] = useState<string[]>(() => {
    const baseTime = Date.now();
    return (domains || []).map(
      (domain, index) => `domain_${baseTime}_${index}_${domain}`
    );
  });
  const [domainValues, setDomainValues] = useState<Record<string, string>>(
    () => {
      const values: Record<string, string> = {};
      const baseTime = Date.now();
      (domains || []).forEach((domain, index) => {
        const id = `domain_${baseTime}_${index}_${domain}`;
        values[id] = domain;
      });
      return values;
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update when domains prop changes
  useEffect(() => {
    const baseTime = Date.now();
    const newIds = (domains || []).map(
      (domain, index) => `domain_${baseTime}_${index}_${domain}`
    );
    const newValues: Record<string, string> = {};
    (domains || []).forEach((domain, index) => {
      const id = `domain_${baseTime}_${index}_${domain}`;
      newValues[id] = domain;
    });
    setDomainIds(newIds);
    setDomainValues(newValues);
  }, [domains]);

  const handleAddDomain = () => {
    // Check if the last domain is complete before adding a new one
    if (domainIds.length > 0) {
      const lastId = domainIds[domainIds.length - 1];
      const lastDomain = domainValues[lastId]?.trim() || '';

      if (!lastDomain) {
        toast({
          variant: 'error',
          description: 'Please enter a domain before adding a new one'
        });
        return;
      }

      // Validate the domain format
      const domainRegex =
        /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      if (!domainRegex.test(lastDomain) && !lastDomain.startsWith('http')) {
        toast({
          variant: 'error',
          description: 'Please enter a valid domain before adding a new one'
        });
        return;
      }
    }

    const newId = `domain_${Date.now()}`;
    setDomainIds([...domainIds, newId]);
    setDomainValues({
      ...domainValues,
      [newId]: ''
    });
  };

  const handleRemoveDomain = (id: string) => {
    const newValues = { ...domainValues };
    delete newValues[id];
    setDomainIds(domainIds.filter((did) => did !== id));
    setDomainValues(newValues);
    // Clear error for this domain
    if (errors[id]) {
      const newErrors = { ...errors };
      delete newErrors[id];
      setErrors(newErrors);
    }
  };

  const handleUpdateDomain = (id: string, value: string) => {
    setDomainValues({
      ...domainValues,
      [id]: value
    });
    // Clear error for this domain
    if (errors[id]) {
      const newErrors = { ...errors };
      delete newErrors[id];
      setErrors(newErrors);
    }
  };

  const validateDomain = (domain: string): string | null => {
    if (!domain.trim()) {
      return 'Domain is required';
    }
    // Simple domain validation
    const domainRegex =
      /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domain) && !domain.startsWith('http')) {
      return 'Please enter a valid domain';
    }
    return null;
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};
    const domainsArray: string[] = [];

    // Validate all domains
    domainIds.forEach((id) => {
      const domain = domainValues[id]?.trim() || '';
      const error = validateDomain(domain);
      if (error) {
        newErrors[id] = error;
      } else if (domain) {
        // Check for duplicates
        if (domainsArray.includes(domain)) {
          newErrors[id] = 'Duplicate domain';
        } else {
          domainsArray.push(domain);
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSave(domainsArray);
    toast({
      variant: 'success',
      description: 'Whitelisted domains saved successfully'
    });
  };

  const handleClear = () => {
    const hasData = domainIds.length > 0;
    setDomainIds([]);
    setDomainValues({});
    setErrors({});

    if (hasData) {
      toast({
        variant: 'default',
        description: 'Whitelisted domains cleared'
      });
    }
  };

  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 700,
          maxWidth: '90vw',
          maxHeight: '90vh'
        }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Configure Whitelisted Domains</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        <Box
          css={{
            padding: '$12',
            maxHeight: 'calc(90vh - 180px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '$neutral100',
              borderRadius: '$md'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '$neutral300',
              borderRadius: '$md',
              '&:hover': {
                background: '$neutral400'
              }
            }
          }}
        >
          <Flex flexDirection="column" gap="$6">
            <Flex flexDirection="column" gap="$4">
              {domainIds.map((id) => (
                <Flex key={id} gap="$2" alignItems="flex-start">
                  <FormInput
                    placeholder="Enter domain (e.g., example.com)"
                    size="lg"
                    value={domainValues[id] || ''}
                    onChange={(e: any) =>
                      handleUpdateDomain(id, e.target.value)
                    }
                    css={{ flex: 1 }}
                    error={errors[id]}
                  />
                  <IconButton
                    size="md"
                    variant="ghost"
                    color="error"
                    icon={<DeleteIcon size={16} />}
                    onClick={() => handleRemoveDomain(id)}
                    css={{ marginTop: '$1' }}
                  />
                </Flex>
              ))}
              {domainIds.length === 0 && (
                <Text
                  size="sm"
                  css={{
                    color: '$neutral600',
                    textAlign: 'center',
                    padding: '$8'
                  }}
                >
                  No domains configured. Click "Add Domain" to add one.
                </Text>
              )}
            </Flex>
            <Button
              variant="outline"
              size="md"
              leftIcon={<PlusIcon />}
              onClick={handleAddDomain}
              css={{ width: 'max-content', alignSelf: 'flex-start' }}
            >
              Add Domain
            </Button>
          </Flex>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          css={{
            padding: '$12',
            paddingTop: '$8',
            gap: '$4',
            borderTop: '$borderWidths$xs solid $neutral200'
          }}
        >
          <Button
            size="lg"
            variant="outline"
            color="error"
            onClick={handleClear}
          >
            Clear
          </Button>
          <Flex gap="$4">
            <Button size="lg" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button size="lg" color="secondary" onClick={handleSave}>
              Save
            </Button>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default AppDetails;
